import { Fragment, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { ChevronUpDownIcon, BuildingOffice2Icon, CheckIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useGetAvailableClients, useSwitchUserClient } from "../../queries/admin";
import { useAuthStore } from "../../store/useAuthStore";
import { decodeAuthToken } from "../../utils/auth";
import { useLocationStore } from "../../store/useLocationStore";
import { useDeviceStore } from "../../store/useDeviceStore";
import { queryClient } from "../../queries/client";
import { ORG_SWITCH_FLAG } from "../../constant";
import AddCompanyModal from "../modal/AddCompanyModal";

export default function OrgSwitcher() {
  const { clientId, setAuth } = useAuthStore();
  const { clearSelectedLocation } = useLocationStore();
  const { clearSelectedDevice } = useDeviceStore();
  const { data: clients } = useGetAvailableClients();
  const { mutate: switchClient } = useSwitchUserClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const safeClients = clients ?? [];
  const currentClient = safeClients.find((c) => c.id === clientId);
  const hasClients = safeClients.length > 0;

  const handleSwitch = (newClientId: string) => {
    if (newClientId === clientId) return;
    setIsSwitching(true);
    switchClient(newClientId, {
      onSuccess: (data: any) => {
        // Update auth store with new token and clientId
        const responseData = data.data ?? data;
        if (responseData.token) {
          // The new token carries the role the user holds in THIS org
          // (UserClient.role). Decode it and refresh role/email/id too —
          // otherwise the stale role persists and the UI keeps the old
          // org's permissions after the reload.
          const decoded = decodeAuthToken(responseData.token);
          setAuth({
            accessToken: responseData.token,
            clientId: responseData.clientId ?? decoded?.clientId ?? newClientId,
            ...(decoded
              ? { role: decoded.role, email: decoded.email, id: decoded.userId }
              : {}),
          });
          localStorage.setItem("accesstoken", responseData.token);
        } else {
          setAuth({ clientId: newClientId });
        }
        // Clear all org-specific cached/persisted data
        clearSelectedLocation();
        clearSelectedDevice();
        queryClient.clear();
        // Tell the dashboard to keep its loader up after the reload
        sessionStorage.setItem(ORG_SWITCH_FLAG, "1");
        window.location.reload();
      },
      onError: () => {
        setIsSwitching(false);
      },
    });
  };

  return (
    <Menu as="div" className="relative">
      <MenuButton className="h-10 inline-flex items-center gap-2 cursor-pointer rounded-lg bg-white dark:bg-gray-800 py-1.5 pl-3 pr-3 text-left text-gray-900 dark:text-gray-200 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:outline-none focus:ring-2 focus:ring-primary text-sm sm:leading-6 font-medium transition-all">
        <BuildingOffice2Icon className="h-4 w-4 text-gray-400" />
        <span className="max-w-[140px] truncate">
          {currentClient?.name ?? (hasClients ? "Select Organization" : "No Organization")}
        </span>
        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
      </MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg py-1 focus:outline-none">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {hasClients ? "Switch Organization" : "Organizations"}
            </p>
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {hasClients ? (
              safeClients.map((client) => (
                <MenuItem key={client.id}>
                  <button
                    onClick={() => handleSwitch(client.id)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700"
                  >
                    {client.logo ? (
                      <img
                        src={client.logo}
                        alt={client.name}
                        className="h-6 w-6 rounded-full object-cover"
                      />
                    ) : (
                      <BuildingOffice2Icon className="h-6 w-6 text-gray-400" />
                    )}
                    <span className="flex-1 truncate text-left">{client.name}</span>
                    {client.id === clientId && (
                      <CheckIcon className="h-4 w-4 text-primary" />
                    )}
                  </button>
                </MenuItem>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                You don't belong to any organization yet.
              </div>
            )}
          </div>
          {/* Any user can create their own organization — they become its admin. */}
          <div className="border-t border-gray-100 dark:border-gray-700 py-1">
            <MenuItem>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700"
              >
                <PlusIcon className="h-5 w-5 text-gray-400" />
                <span>Create Organization</span>
              </button>
            </MenuItem>
          </div>
        </MenuItems>
      </Transition>

      {showAddModal && (
        <AddCompanyModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Covers the gap between clicking an org and the post-switch reload */}
      {isSwitching &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white dark:bg-gray-900">
            <div className="relative">
              <div className="h-16 w-16 rounded-full border-4 border-gray-200 dark:border-gray-700" />
              <div className="absolute top-0 h-16 w-16 animate-spin rounded-full border-4 border-transparent border-t-primary" />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-600 dark:text-gray-400">
              Switching organization...
            </p>
          </div>,
          document.body,
        )}
    </Menu>
  );
}
